import type {
  ContractHistoryQueryParams,
  ContractHistoryResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractHistoryResponseSchema } from './validations';

/**
 * Fetches the version history for a contract.
 *
 * @param contractId - The UUID of the contract.
 * @param queryParams - Pagination parameters (limit, offset).
 * @param options - Additional request options.
 * @returns The paginated history entries.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_history_contracts__contract_id__history_get
 */
export const fetchContractHistory = async (
  contractId: UUID,
  queryParams?: ContractHistoryQueryParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT.GET_HISTORY(contractId), {
      searchParams: queryParams,
      ...options,
    })
    .json<ContractHistoryResponse>();

  const validatedResponse =
    zodContractHistoryResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      error: {
        errorMessage: validatedResponse?.error?.message,
        errors: validatedResponse?.error?.issues,
        response,
      },
      message: 'Contract History API response has validation errors',
    });
  }

  return response;
};
